import React, { useState, useEffect, useCallback, useRef } from 'react'
import DashboardLayout from '../../../layouts/Dashboard'
import {
  Col,
  Row,
  Badge,
  Spinner,
  Form,
  Modal,
  Button,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap'
import { Warning, Funnel, X, ArrowLeft } from 'phosphor-react'
import { toast } from 'react-toastify'
import api from '../../../services/api'

import { formatDateTSX } from '../../../utils/tsxFormat'
import styles from './styles.module.css'
import { useLoading } from '../../../hooks/LoadingContext'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'
import RequestModal from '../../../components/RequestModal'
import SearchInput from '../../../components/SearchInput'

import { useLocation } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

interface IRequest {
  id: number
  invoice: string
  request_number: string
  invoice_number: string
  receiver_name: string
  status: string
  cep: string
  city: string
  state: string
  neighborhood: string
  street: string
  street_number: string
  complement: string
  weight: number
  start_date: Date
  created_at: string
  updated_at: string
  delivering_image_url: string
  delivering_image_key: string
  customer_rating_stars: number
  customer_rating_text: string
  request_delivery_driver_note: string
  request_delivery_time_seconds: number
  request_delivery_distance_meters: number
  vehicle_id: number
  vehicle_board: string
  delivery_id?: number
  driver_id: number
  driver_name: string
  delivery_had_problems: boolean
  note: string
  delivery_problem_solution_note: string
  is_internal_transfer: boolean
}

const status = {
  unallocated: { name: 'Não alocado', color: '#cca300' },
  allocated: { name: 'Alocado', color: '#00BFFF' },
  on_route: { name: 'Em rota', color: '#053C5E' },
  delivered: { name: 'Entregue', color: '#089c08' },
  canceled: { name: 'Cancelado', color: '#ff0000' },
}

const AtendenteEntregas: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate() // Hook de navegação

  const [requests, setRequests] = useState<IRequest[]>()
  const [unallocatedRequests, setUnallocatedRequests] = useState<IRequest[]>()

  const { isLoading, setIsLoading } = useLoading()
  const [hasMoreData, setHasMoreData] = useState<boolean>(true)

  const [lastSearch, setLastSearch] = useState('')
  const [filteredStatus, setFilteredStatus] = useState('')
  const [search, setSearch] = useState(() => {
    const queryParams = new URLSearchParams(location.search)
    if (queryParams) {
      return queryParams.get('predefinedSearch') ?? ''
    }
    return ''
  })

  const [requestsChanged, setRequestsChanged] = useState<boolean>(false)
  const [showRequestModal, setShowRequestModal] = useState<boolean>(false)
  const [requestModal, setRequestModal] = useState<IRequest>()
  const [filterModal, setFilterModal] = useState(false)

  const cardsContainerRef = useRef<any>()

  const deliveryStatusMap: any = {
    allocated: 'Alocada',
    on_route: 'Em rota',
    delivered: 'Entregue',
    canceled: 'Cancelada',
  }

  const loadData = useCallback(async () => {
    const isSearchDifferent = search !== lastSearch
    if (!hasMoreData && !isSearchDifferent) return

    setIsLoading(true)

    if (isSearchDifferent) {
      setFilteredStatus('')
      setLastSearch(search)
      setHasMoreData(true)
    }

    let apiUrl =
      '/attendant/requests/delivery?status=allocated,on_route,delivered,canceled&limit=20'
    if (search) apiUrl += '&search=' + search

    const offset = requests && !isSearchDifferent ? requests.length : 0
    if (offset) apiUrl += '&offset=' + offset

    try {
      const { data } = await api.get(apiUrl)
      if (!data.length) {
        if (isSearchDifferent) setRequests([])
        setHasMoreData(false)
      } else {
        if (!requests) setRequests(data)
        else setRequests(isSearchDifferent ? data : requests.concat(data))
      }

      if (isSearchDifferent) {
        if (search) {
          //busca dados de requisicoes ainda NÃO alocadas também, limit 8
          const unallocatedRequests = await api.get(
            `attendant/requests?status=unallocated&limit=8&search=${search}`,
          )
          setUnallocatedRequests(unallocatedRequests.data)
        } else setUnallocatedRequests([])
      }
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
    }
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, requests, hasMoreData])

  //useEffect do loading inicial dos dados
  useEffect(() => {
    if (!requests && hasMoreData) loadData()
  }, [loadData, hasMoreData, requests])

  const handleScroll = () => {
    const element = cardsContainerRef.current
    if (!element) return

    // Checa se a rolagem atingiu o final
    if (element.scrollHeight - element.scrollTop === element.clientHeight) {
      loadData()
    }
  }

  useEffect(() => {
    const interval = setInterval(async () => {
      let apiUrl = `/attendant/requests/delivery?status=allocated,on_route,delivered,canceled&limit=${requests?.length}`

      if (search) apiUrl += '&search=' + search

      setIsLoading(true)
      const { data } = await api.get(apiUrl)
      if (JSON.stringify(data) !== JSON.stringify(requests)) {
        setRequestsChanged(true)
        setRequests(data)
      }
      setIsLoading(false)
    }, 30000)

    return () => clearInterval(interval)
  }, [requests, search, setIsLoading])

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Entregas">
      <LoadingSpinner text="Buscando entregas. Aguarde..." />

      {/* Botão de Voltar para a tela inicial */}
      <div style={{ marginLeft: 30, marginBottom: 30 }}>
        <Button
          variant="outline-primary"
          className={`${styles.backButton} ${styles.backBtn}`}
          onClick={() => navigate('/')}
        >
          <ArrowLeft size={18} weight="bold" />
          Voltar
        </Button>
      </div>

      <Form style={{ color: '#A59E9E' }}>
        <Row style={{ marginTop: '10px', marginInline: 15 }}>
          <SearchInput
            placeholder="Digite o nome do cliente, nº do pedido/nota, código fiscal, placa do veículo ou nome do motorista"
            searchValue={search}
            setValue={setSearch}
            handleSearch={loadData}
            showOptions={false}
          />
          <Col
            className="d-flex justify-content-md-end justify-content-center align-items-center mt-md-0 mt-3"
            md={12}
            lg={4}
          >
            <Button className={styles.filterBtn} onClick={() => setFilterModal(true)}>
              <span style={{ fontSize: 14 }}>Filtrar</span> <Funnel size={20} />
            </Button>
          </Col>
        </Row>
      </Form>
      {requestsChanged && (
        <Row>
          <Badge
            bg=""
            className={
              styles.infoBadge +
              ' d-flex flex-md-row flex-column justify-content-center align-items-center'
            }
          >
            <p className="my-0">
              As entregas foram atualizadas com novas alterações. Confira-as abaixo.
            </p>
            <span className="m-2 my-0">
              <p className="m-0">
                <Button
                  color="white"
                  className={styles.closeNotificationButton + ' py-0 px-1 mt-md-0 mt-2'}
                  onClick={() => setRequestsChanged(false)}
                >
                  Fechar
                  <X size={15} weight="bold" style={{ paddingBottom: '2px' }} />
                </Button>
              </p>
            </span>
          </Badge>
        </Row>
      )}
      <Row
        onScroll={handleScroll}
        className={`${styles.cardsContainer} ml-2 mt-1`}
        ref={cardsContainerRef}
      >
        {isLoading && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 40,
            }}
          >
            <Spinner
              animation="border"
              style={{
                color: '#053C5E',
                marginRight: 10,
              }}
            />
          </div>
        )}
        {!requests?.length && !isLoading && (
          <Badge
            bg=""
            text="white"
            style={{ textAlign: 'center', marginTop: 40, backgroundColor: '#053c5e' }}
          >
            <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" /> Nenhuma
            entrega já alocada encontrada!
          </Badge>
        )}
        {requests
          ?.filter((r) => r.status === filteredStatus || !filteredStatus)
          .map((request) => (
            <Col key={`request_${request.id}`} className={`${styles.card}`} lg={4} md={5} xl={3}>
              <Col className={styles.cardCol}>
                <div className={styles.cardHeader}>
                  <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip>{`Destinatário: ${request.receiver_name}`}</Tooltip>}
                  >
                    <span className={styles.cardReceiver}>{request.receiver_name}</span>
                  </OverlayTrigger>

                  {request.request_number && (
                    <span className={styles.cardRequestNumber}>
                      Pedido {request.request_number}
                    </span>
                  )}
                </div>
                <div className={styles.cardBody}>
                  <span className={styles.startDate}>
                    {`De: ${formatDateTSX(request.created_at, 'dd/MM/yyyy', false)}`}
                  </span>
                  <span className={styles.infoSpan}>
                    <strong>Veículo:</strong> {request.vehicle_board}
                  </span>
                  <span className={styles.infoSpan}>
                    <strong>Motorista:</strong> {request?.driver_name || 'Não informado'}
                  </span>
                </div>
                <div className={styles.cardFooter}>
                  <span
                    className={styles.infoSpan}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 3,
                    }}
                  >
                    <strong>Status: </strong>
                    <Badge
                      bg="unset"
                      color="white"
                      pill
                      className={styles.statusBadge}
                      style={{
                        backgroundColor: status[request.status as keyof typeof status].color,
                      }}
                    >
                      {status[request.status as keyof typeof status].name}
                    </Badge>
                  </span>
                  <Button
                    className={styles.detailsButton}
                    onClick={() => [setRequestModal(request), setShowRequestModal(true)]}
                  >
                    Ver detalhes do pedido
                  </Button>
                </div>
              </Col>
            </Col>
          ))}
      </Row>
      <div className={`${styles.header} text-center text-md-left ml-md-0 px-4`}>
        <p className="mt-3">Pedidos ainda não alocados</p>
      </div>

      {!search &&
        (!unallocatedRequests || (unallocatedRequests && unallocatedRequests.length === 0)) && (
          <div className="text-center">
            <span className={`${styles.modalInfoBadge}`}>
              Pesquise pelo nome do cliente, nº do pedido/nota, código fiscal, placa do veículo ou
              nome do motorista para consultar pedidos ainda não alocados.
            </span>
          </div>
        )}

      {search &&
        lastSearch &&
        (!unallocatedRequests || (unallocatedRequests && unallocatedRequests.length === 0)) && (
          <div className="text-center px-3 mt--8" style={{ overflowX: 'scroll' }}>
            <Badge
              bg=""
              text="white"
              style={{ textAlign: 'center', width: '100%', backgroundColor: '#053c5e' }}
            >
              <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" /> Nenhum
              pedido ainda desalocado encontrado!
            </Badge>
          </div>
        )}

      <Row className={`${styles.unallocatedCardsContainer} ml-2`}>
        {unallocatedRequests?.map((request) => (
          <Col
            key={`unallocatedrequest_${request.id}`}
            className={styles.card}
            md={5}
            lg={4}
            xl={3}
          >
            <Col className={styles.cardCol}>
              <div className={styles.cardHeader}>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>{`Destinatário: ${request.receiver_name}`}</Tooltip>}
                >
                  <span className={styles.cardReceiver}>{request.receiver_name}</span>
                </OverlayTrigger>

                {request.request_number && (
                  <span className={styles.cardRequestNumber}>Pedido {request.request_number}</span>
                )}
              </div>
              <div className={styles.cardBody}>
                <span className={styles.startDate}>
                  {`De: ${formatDateTSX(request.created_at, 'dd/MM/yyyy', false)}`}
                </span>
                <span className={styles.infoSpan}>
                  <strong>Veículo:</strong> {request.vehicle_board}
                </span>
                <span className={styles.infoSpan}>
                  <strong>Motorista:</strong> {request?.driver_name || 'Não informado'}
                </span>
              </div>
              <div className={styles.cardFooter}>
                <span className={styles.infoSpan}>
                  <strong>Status: </strong>
                  <Badge
                    bg="unset"
                    color="white"
                    pill
                    className={styles.statusBadge}
                    style={{
                      backgroundColor: status[request.status as keyof typeof status].color,
                    }}
                  >
                    {status[request.status as keyof typeof status].name}
                  </Badge>
                </span>
                <Button
                  className={styles.detailsButton}
                  onClick={() => [setRequestModal(request), setShowRequestModal(true)]}
                >
                  Ver detalhes do pedido
                </Button>
              </div>
            </Col>
          </Col>
        ))}
      </Row>

      {requestModal && (
        <RequestModal
          request={requestModal}
          userRole="attendant"
          showEditButton={false}
          showRequestModal={showRequestModal}
          setShowRequestModal={setShowRequestModal}
        ></RequestModal>
      )}
      <Modal centered show={filterModal} onHide={() => setFilterModal(false)}>
        <Modal.Header closeButton />
        <Modal.Body>
          <span style={{ fontSize: 14, fontWeight: '500' }} className="text-left text-dark">
            Filtrar por status
          </span>
          <Row>
            <Col>
              <Form.Select
                className={styles.statusSelect}
                onChange={(event) => {
                  setFilteredStatus(event.target.value)
                  setFilterModal(false)
                }}
              >
                <option value="">Selecione uma opção</option>
                {Object.keys(deliveryStatusMap).map((key, index) => (
                  <option key={'map_' + index} value={key}>
                    {deliveryStatusMap[key]}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          <Row className="text-center mt-2">
            <Col
              style={{
                fontSize: 20,
                fontWeight: 'bold',
                color: '#053C5E',
              }}
            >
              <Button
                className={styles.clearFiltersBtn}
                onClick={() => {
                  setFilteredStatus('')
                  setFilterModal(false)
                }}
              >
                Limpar filtros
              </Button>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </DashboardLayout>
  )
}

export default AtendenteEntregas
